import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import {
  Autocomplete,
  Box,
  Container,
  Grid,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import backArrow from "../../../assets/icons/backArrow.svg";
import { CustomButton, CustomTable } from "../../../global/components";
import CustomLoader from "../../../global/components/CustomLoader/CustomLoader";
import strings from "../../../global/constants/StringConstants";
import {
  handleErrorMessage,
  openSuccessNotification,
} from "../../../helpers/methods";
import history from "../../../utils/history";
import { theme } from "../../../utils/styles";
import { getProfile } from "../../Dashboard/DashboardService";
import dataDownloadStyles from "./DataDownload.styles";
import { downloadData } from "./DataDownloadService";

const DataDownload = () => {
  const classes = dataDownloadStyles;
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [profile, setProfile] = useState<string>("");
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const [selectedDataTypes, setSelectedDataTypes] = useState<string[]>([
    "planet_details",
    "houses_details",
    "analysis",
    "missing_minerals",
    "mahadasha",
    "nakshatra_analysis",
    "current_nakshatra",
    "rahu_transits",
    "arudha",
    "shadbala",
  ]);

  const dataDownloadList = [
    {
      id: "planet_details",
      displayName: "Planet Details",
    },
    {
      id: "houses_details",
      displayName: "Houses Details",
    },
    {
      id: "analysis",
      displayName: "Additional Data",
    },
    {
      id: "missing_minerals",
      displayName: "Missing Minerals",
    },
    {
      id: "mahadasha",
      displayName: "Mahadasha",
    },
    {
      id: "nakshatra_analysis",
      displayName: "Nakshatra Analysis",
    },
    {
      id: "current_nakshatra",
      displayName: "Current Nakshatra",
    },
    {
      id: "rahu_transits",
      displayName: "Rahu Transits",
    },
    {
      id: "arudha",
      displayName: "Arudha",
    },
    {
      id: "shadbala",
      displayName: "Shadbala",
    },
  ];

  const tableHeader = [
    {
      name: `${t("name")}`,
      field: "displayName",
    },
  ];

  const downloadMenuItems = [
    {
      label: "PDF",
      value: "pdf",
      icon: <PictureAsPdfOutlinedIcon sx={{ mr: 1, color: "#222222" }} />,
    },
    {
      label: "ZIP",
      value: "zip",
      icon: <FolderZipOutlinedIcon sx={{ mr: 1, color: "#222222" }} />,
    },
  ];

  useEffect(() => {
    getProfiles();
  }, []);

  const getProfiles = async () => {
    try {
      setIsLoading(true);
      const response = await getProfile();
      setProfiles(response);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const handleDataTypeSelection = (event: any, row: any) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setSelectedDataTypes((prev) => [...prev, row.id]);
    } else {
      setSelectedDataTypes((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const isSelected = (id: string) => {
    const row = dataDownloadList.find((item) => item.id === id);
    return row ? selectedDataTypes.includes(row.id) : false;
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select all data types
      const allIds = dataDownloadList.map((item) => item.id);
      setSelectedDataTypes(allIds);
    } else {
      // Deselect all
      setSelectedDataTypes([]);
    }
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!profile || selectedDataTypes.length === 0) {
      return;
    }
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleDownload = async (format: string) => {
    if (!profile || selectedDataTypes.length === 0) {
      return;
    }
    try {
      setIsLoading(true);
      const language = i18n.language.toLowerCase();
      const blob = await downloadData(
        profile,
        selectedDataTypes,
        language,
        format,
      );

      // Get profile name for filename
      const selectedProfile = profiles.find((p) => p.profileId === profile);
      const profileName = selectedProfile?.name || "data";
      const fileExtension = format === "pdf" ? "pdf" : "zip";
      const fileName = `${profileName}_data_${new Date().getTime()}.${fileExtension}`;

      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      openSuccessNotification(
        "Your data download has started and will complete automatically. You can continue working while it downloads in the background.",
        "Download Started",
      );
      setMenuAnchorEl(null);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getHeader = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { sm: "center", xs: "baseline" },
          width: "100%",
        }}
        pb={3}
      >
        <Box
          display={"flex"}
          alignItems={"center"}
          sx={{ cursor: "pointer", width: "fit-content" }}
          onClick={() => history.goBack()}
        >
          {isMobile ? (
            <img src={backArrow} height={"20px"} alt="Module not found..." />
          ) : (
            <img src={backArrow} height={"25px"} alt="Module not found..." />
          )}
        </Box>
        <Typography sx={classes.italicTypo}>{t("dataDownload")}</Typography>
      </Box>
    );
  };

  const getActions = () => {
    return (
      <Stack
        direction="row"
        justifyContent="space-between"
        p={1}
        position="relative"
      >
        <Autocomplete
          sx={classes.autoComplete}
          options={profiles || []}
          getOptionLabel={(option: any) => option.name || ""}
          renderInput={(params) => (
            <TextField {...params} placeholder={t("searchProfile")} />
          )}
          onChange={(event, value) => {
            setProfile(value?.profileId || "");
          }}
        />
        <CustomButton
          label={t("download")}
          type={strings.PRIMARY}
          onClick={handleDownloadClick}
          disabled={!profile || selectedDataTypes.length === 0}
          customClasses={{ maxWidth: "300px", minHight: "46px" }}
        />
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 1,
              minWidth: 150,
              borderRadius: 2,
              boxShadow: "0px 2px 8px rgba(0,0,0,0.32)",
            },
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          {downloadMenuItems.map((item) => (
            <MenuItem
              key={item.value}
              onClick={() => handleDownload(item.value)}
            >
              {item.icon}
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </Stack>
    );
  };

  const getTable = () => {
    return (
      <Box p={1}>
        <Grid container>
          <Grid item sm={12} sx={{ overflowX: "auto", overflowY: "hidden" }}>
            <CustomTable
              headers={tableHeader}
              rows={dataDownloadList}
              checkboxSelection
              isSelected={(id: string) => isSelected(id)}
              handleClick={handleDataTypeSelection}
              onSelectAllClick={handleSelectAll}
              isSelectAll={selectedDataTypes}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  const getDataDownload = () => {
    return (
      <>
        <Box sx={classes.mainContainer}>
          <Box sx={classes.mainBox}>
            <Box sx={classes.subBox}>
              <Box sx={classes.scrollStyle}>
                <Container maxWidth="xl" sx={{ px: 1 }}>
                  {getHeader()}
                  {getActions()}
                  {getTable()}
                </Container>
              </Box>
            </Box>
          </Box>
        </Box>
        <CustomLoader isLoading={isLoading} />
      </>
    );
  };
  return getDataDownload();
};

export default DataDownload;
